import React, { memo, useMemo, useCallback } from 'react';
import {
  FaFacebook, 
  FaInstagram, 
  FaYoutube,
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaBriefcase, 
  FaUsers, 
  FaShieldAlt, 
  FaInfoCircle, 
  FaHandsHelping, 
  FaQuestionCircle,
  FaWhatsapp,
  FaClock,
  FaHeart,
  FaNewspaper
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import ScrollLink from './ScrollLink';
import { COMPANY_INFO, SOCIAL_LINKS, BUSINESS_HOURS } from '../constants';

// Componente de ítem con icono memoizado
const IconLinkItem = memo(({ to, href, icon: Icon, text, external = false, onClick, className = "" }) => {
  const baseClasses = "inline-flex items-center group transition-all duration-300 hover:text-white hover:translate-x-1";
  const combinedClasses = `${baseClasses} ${className}`;

  if (external) {
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className={combinedClasses}
        aria-label={text}
      >
        <Icon className="mr-3 text-blue-400 group-hover:text-white transition-colors duration-300" />
        <span className="relative">
          {text}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300"></span>
        </span>
      </a>
    );
  }

  if (onClick) {
    return (
      <Link to={to} className={combinedClasses} onClick={onClick}>
        <Icon className="mr-3 text-blue-400 group-hover:text-white transition-colors duration-300" />
        <span className="relative">
          {text}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300"></span>
        </span>
      </Link>
    );
  }

  return (
    <ScrollLink to={to} className={combinedClasses}>
      <Icon className="mr-3 text-blue-400 group-hover:text-white transition-colors duration-300" />
      <span className="relative">
        {text}
        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300"></span>
      </span>
    </ScrollLink>
  );
});

IconLinkItem.displayName = 'IconLinkItem';

// Componente de ítem de contacto memoizado
const ContactItem = memo(({ href, icon: Icon, text, color, label, tel = false, email = false }) => {
  const handleClick = useCallback((e) => {
    if (tel) {
      e.preventDefault();
      window.open(`tel:${href.replace('tel:', '')}`);
    } else if (email) {
      e.preventDefault();
      window.open(`mailto:${href.replace('mailto:', '')}`);
    }
  }, [href, tel, email]);

  return (
    <li>
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="flex items-start group transition-all duration-300 hover:text-white hover:translate-x-1 py-1"
        aria-label={label}
        onClick={handleClick}
      >
        <Icon className={`mr-3 mt-1 ${color} group-hover:text-white transition-colors duration-300 flex-shrink-0`} />
        <span 
          className="leading-relaxed"
          dangerouslySetInnerHTML={{ __html: text }}
        />
      </a>
    </li>
  );
});

ContactItem.displayName = 'ContactItem';

// Componente de red social memoizado
const SocialLink = memo(({ href, icon: Icon, label, hoverColor, bgColor }) => (
  <li>
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer" 
      className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${bgColor} text-white transition-all duration-300 transform hover:scale-110 hover:shadow-lg ${hoverColor} group`}
      aria-label={`Síguenos en ${label}`}
    >
      <Icon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
    </a>
  </li>
));

SocialLink.displayName = 'SocialLink';

function Footer() {
  const currentYear = new Date().getFullYear();

  // Datos memoizados para evitar recreación en cada render
  const socialLinks = useMemo(() => [
    { 
      href: SOCIAL_LINKS.facebook, 
      icon: FaFacebook, 
      label: "Facebook", 
      hoverColor: "hover:shadow-blue-500/50",
      bgColor: "bg-blue-600 hover:bg-blue-700"
    },
    { 
      href: SOCIAL_LINKS.instagram, 
      icon: FaInstagram, 
      label: "Instagram", 
      hoverColor: "hover:shadow-pink-500/50",
      bgColor: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
    },
    { 
      href: SOCIAL_LINKS.youtube, 
      icon: FaYoutube, 
      label: "YouTube", 
      hoverColor: "hover:shadow-red-500/50",
      bgColor: "bg-red-600 hover:bg-red-700"
    }
  ], []);

  const quickLinks = useMemo(() => [
    { to: "/about-us", text: "Sobre nosotros", icon: FaInfoCircle },
    { to: "/services", text: "Servicios", icon: FaHandsHelping },
    { to: "/FAQs", text: "Preguntas frecuentes", icon: FaQuestionCircle },
    { to: "/blog", text: "Blog", icon: FaNewspaper }
  ], []);

  const legalLinks = useMemo(() => [
    { to: "/privacy-policy", text: "Política de privacidad", icon: FaShieldAlt },
    { to: "/terms-and-conditions", text: "Términos de servicio", icon: FaUsers },
    { to: "/careers", text: "Contratación", icon: FaBriefcase }
  ], []);

  const contactInfo = useMemo(() => [
    {
      href: COMPANY_INFO.mapsUrl,
      icon: FaMapMarkerAlt,
      text: COMPANY_INFO.address.replace(', ', ',<br />'),
      color: "text-blue-400",
      label: "Ver ubicación en Google Maps"
    },
    {
      href: `tel:+52${COMPANY_INFO.phone}`,
      icon: FaPhone,
      text: COMPANY_INFO.phoneFormatted,
      color: "text-green-400",
      label: "Llamar por teléfono",
      tel: true
    },
    {
      href: `https://wa.me/${COMPANY_INFO.whatsapp}`,
      icon: FaWhatsapp,
      text: `WhatsApp: ${COMPANY_INFO.phoneFormatted}`,
      color: "text-green-500",
      label: "Contactar por WhatsApp"
    },
    {
      href: `mailto:${COMPANY_INFO.email}`,
      icon: FaEnvelope,
      text: COMPANY_INFO.email,
      color: "text-blue-300",
      label: "Enviar correo electrónico",
      email: true
    }
  ], []);

  // Usar BUSINESS_HOURS desde constantes

  const handleScrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-300 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-600 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

            {/* Contacto */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <FaPhone className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white text-xl font-bold">Contacto</h3>
              </div>
              <ul className="space-y-4">
                {contactInfo.map((item, i) => (
                  <ContactItem key={i} {...item} />
                ))}
              </ul>
            </div>

            {/* Horarios de Atención */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-600 rounded-lg">
                  <FaClock className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white text-xl font-bold">Horarios</h3>
              </div>
              <ul className="space-y-3">
                {BUSINESS_HOURS.map((schedule, i) => (
                  <li key={i} className="flex justify-between items-center py-2 border-b border-gray-700/50">
                    <span className="font-medium text-gray-300">{schedule.day}</span>
                    <span className="text-green-400 font-semibold">{schedule.time}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4 mt-6">
                <p className="text-green-400 text-sm font-medium">
                  🟢 Disponibles las 24 horas, todos los días del año
                </p>
              </div>
            </div>

            {/* Enlaces rápidos */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-700 rounded-lg">
                  <FaHandsHelping className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white text-xl font-bold">Enlaces rápidos</h3>
              </div>
              <nav aria-label="Enlaces rápidos">
                <ul className="space-y-4">
                  {quickLinks.map((link, i) => (
                    <li key={i}>
                      <IconLinkItem {...link} />
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Legal Links */}
              <div className="mt-8 pt-6 border-t border-gray-700/50">
                <h4 className="text-white font-semibold mb-4">Legal</h4>
                <nav aria-label="Enlaces legales">
                  <ul className="space-y-3">
                    {legalLinks.map((link, i) => (
                      <li key={i}>
                        <IconLinkItem {...link} onClick={handleScrollToTop} />
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>

            {/* Redes sociales y Newsletter */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-800 rounded-lg">
                  <FaHeart className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white text-xl font-bold">Síguenos</h3>
              </div>
              
              <p className="text-gray-400 mb-6 leading-relaxed">
                Mantente conectado con nosotros para recibir consejos de salud, 
                noticias y actualizaciones de nuestros servicios.
              </p>

              <nav aria-label="Redes sociales">
                <ul className="flex space-x-4 mb-8">
                  {socialLinks.map((social, i) => (
                    <SocialLink key={i} {...social} />
                  ))}
                </ul>
              </nav>

              {/* Call to Action */}
              <div className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-6">
                <h4 className="text-white font-bold mb-3">¿Necesitas ayuda?</h4>
                <p className="text-sm text-gray-300 mb-4">
                  Contactanos las 24 horas para atención inmediata
                </p>
                <a
                  href="https://wa.me/523325456522?text=Hola,%20necesito%20información%20sobre%20sus%20servicios"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  Contactar ahora
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-700/50 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-gray-400 text-center md:text-left">
                &copy; {currentYear} <span className="font-semibold text-white">Geriatría Serena</span>. 
                Todos los derechos reservados.
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>Diseñado con</span>
                <FaHeart className="w-4 h-4 text-red-500 animate-pulse" />
                <span>en México</span>
              </div>
            </div>
            
            {/* Additional Info */}
            <div className="mt-6 pt-6 border-t border-gray-800/50 text-center">
              <p className="text-xs text-gray-500 leading-relaxed max-w-4xl mx-auto">
                Geriatría Serena es una empresa dedicada al cuidado integral de personas mayores y pacientes 
                que requieren atención especializada. Nuestros servicios incluyen enfermería profesional, 
                cuidado geriátrico y acompañamiento terapéutico con personal altamente calificado.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
